import NProgress from "nprogress";
import TimeSlotService from "../services/TimeSlotsService";
import _ from "lodash";
import moment from "moment";
import CalendarEventsService from "../services/CalendarEventsService";
import UsersService from "../services/UsersService";
import {filterEventsForDate} from "../include/Calendar";

export const formatTimeslot = (event) => {
    event.resourceId = event.user_id;
    event.jobId = event.job_id;
    event.start = moment(event.start);
    event.startTime = moment(event.start).format('X');
    event.end = moment(event.end);
    event.textColor = "#ffffff";
    event.backgroundColor = event.job.color;
    event.title = event.job.customer.company_name;
    event.hover = false;
    if (event.job.site && event.job.site.address && event.job.site.address.postcode) {
        event.title += ', ' + event.job.site.address.postcode;
    }
    event.type = 'timeslot';
    return event;
}

export default {
    namespaced: true,
    state: {
        startDate: window.moment().startOf('week'),
        view: 'week',
        events: [],
        showJobs: true,
        timeSlots: [],
        timeSlotsLoading: false,
        eventsLoading: false,
        searchParams: {},
        showAllStaff: false,
        resources: [],
    },
    mutations: {
        setShowJobs(state, showJobs) {
            state.showJobs = showJobs;
        },
        setTimeSlotsLoading(state, loading) {
            state.timeSlotsLoading = loading;
        },
        setEventsLoading(state, loading) {
            state.eventsLoading = loading;
        },
        setEvents(state, events) {
            state.events = events;
        },
        setTimeSlots(state, timeSlots) {
            state.timeSlots = timeSlots;
        },
        clearAll(state) {
            state.events = [];
            state.timeSlots = [];
        },
        setResources(state, resources) {
            state.resources = resources;
        },
        setShowAllStaff(state, showAllStaff) {
            state.showAllStaff = showAllStaff;
        },
        setView(state, view) {
            state.view = view;
        },
        setStartDate(state, date) {
            state.startDate = date;
        },
    },
    actions: {
        loadAll({dispatch}) {
            dispatch('loadTimeslots');
            dispatch('loadEvents');
        },
        loadTimeslots({commit, state, getters}) {
            if (state.timeSlotsLoading)  {
                return;
            }
            commit('setTimeSlotsLoading', true);
            TimeSlotService
                .search({
                    'start': state.startDate.format('YYYY-MM-DD HH:mm:ss'),
                    'end': getters.endDate.format('YYYY-MM-DD HH:mm:ss'),
                    include: [
                        "job",
                        "user",
                        "job.customer",
                        "job.site",
                        "job.site.address",
                        "job.timeSlots",
                        "job.timeSlots.user"
                    ]
                })
                .then(
                    xhr => {
                        const timeSlots = _.sortBy(xhr.data.map(event => {
                            return formatTimeslot(event);
                        }), ['order', 'startTime']);

                        commit('setTimeSlots', timeSlots);
                        commit('setTimeSlotsLoading', false);
                        NProgress.done();
                    },
                    () => {
                        commit('setTimeSlotsLoading', false);
                        window.alert("Error: Could not load job time slots");
                    });
        },

        loadEvents({commit, state, getters}) {
            if (state.eventsLoading)  {
                return;
            }
            commit('setEventsLoading', true);
            CalendarEventsService.fetchEvents({
                'start': state.startDate.format('YYYY-MM-DD HH:mm:ss'),
                'end': getters.endDate.format('YYYY-MM-DD HH:mm:ss'),
                calendar: 1,
            }).then(({data}) => {
                const calendarEvents = _.sortBy(data.map(event => {
                    event.resourceId = event.user_id;
                    event.start = moment(event.start_at);
                    event.startTime = moment(event.start_at).format('X');
                    event.end = moment(event.end_at);
                    event.title = event.type;
                    event.hover = false;
                    event.textColor = "#000000";
                    event.borderColor = event.status === 'Approved' ? 'green' : "#000000";
                    event.backgroundColor = '#ffffff';
                    return event;
                }), 'startTime');

                commit('setEvents', calendarEvents);
                commit('setEventsLoading', false);
            });
        },

        loadResources({commit, state}) {
            if (state.showAllStaff) {
                UsersService.search().then(res => {
                    commit('setResources', res.data);
                });
                return;
            }
            UsersService.engineers().then(res => {
                commit('setResources', res.data);
            });
        },

        toggleDay({commit, dispatch, getters}, {date, resource, type}) {
            CalendarEventsService.toggleDay(
                date.format('YYYY-MM-DD 00:00:00'),
                resource.id,
                type,
            ).then(() => {
                dispatch('loadEvents');
            }).catch((e) => {
                window.alert(`Error: Could not toggle day ${type}`);
                console.error(e);
                dispatch('loadEvents');
            });
        },

        next({commit, dispatch, state}) {
            commit('clearAll');
            commit('setStartDate', state.startDate.clone().add(1, state.view));
            dispatch('loadAll');
        },

        prev({commit, dispatch, state}) {
            commit('clearAll');
            commit('setStartDate', state.startDate.clone().subtract(1, state.view));
            dispatch('loadAll');
        },

        today({commit, dispatch, state}) {
            commit('clearAll');
            commit('setStartDate', window.moment().startOf(state.view));
            dispatch('loadAll');
        },
        jumpToDate({commit, dispatch, state}, date) {
            commit('clearAll');
            commit('setStartDate', date.clone().startOf(state.view));
            dispatch('loadAll');
        },
        changeView({commit, dispatch}, view) {
            commit('setView', view);
            dispatch('loadAll');
        }
    },

    getters: {
        dates(state, getters) {
            var dates = [];
            var iDate = state.startDate.clone();
            while(iDate.isSameOrBefore(getters.endDate)) {
                dates.push(iDate.clone());
                iDate.add(1, 'day');
            }

            return dates;
        },
        endDate(state) {
            return state.startDate.clone().endOf(state.view);
        },
        allEvents(state) {
            const timeSlots = state.showJobs ? state.timeSlots : [];
            const events = [...state.events, ...timeSlots].filter(e => !!e);

            return _.uniqBy(events, e => {return e.type+e.id;});
        },
        eventsForDay(state, getters) {
            return (date, resource) => filterEventsForDate(date, resource, getters.allEvents);
        },
        jobsForDay(state) {
            return (date, resource) => filterEventsForDate(date, resource, state.timeSlots);
        },
        getDayEventsByType(state, getters) {
            return (date, resource, type) => getters.allEvents.filter(event => {
                return event.resourceId === resource.id && event.type === type
            }).filter(event => {
                if (event.start.isSame(date, 'day')) {
                    return true;
                }
                return false;
            });
        },
        isDayFull(state, getters) {
            return (date, resource) => getters.getDayEventsByType(date, resource, 'Full Day').length > 0;
        },
        isDayEarlyStart(state, getters) {
            return (date, resource) => getters.getDayEventsByType(date, resource, 'Early Start').length > 0;
        },
        isDayOvernight(state, getters) {
            return (date, resource) => getters.getDayEventsByType(date, resource, 'Overnight').length > 0;
        },
        loading(state) {
            return state.timeSlotsLoading || state.eventsLoading;
        }
    },
}


// WEBPACK FOOTER //
// ./resources/assets/js/store/resourceCalendar.js